import { layoutTokens, typographyTokens } from "@/shared/theme";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Typography } from "@mui/material";
import { formatEvaluation } from "@chessla/shared";
import type { TemporaryPlayController } from "./temporary-play";

const verdictCopy = {
  holds: "Your idea holds up.",
  slips: "There is a stronger option.",
  loses_material: "This idea loses material.",
  forces_mate: "Your idea leads to a forced mate.",
  gets_mated: "Your opponent can force mate.",
} as const;

export const temporaryPlayProgressCopy = (status: TemporaryPlayController["state"]["status"]): string | null =>
  status === "evaluating" ? "Checking your idea…" : null;

export const TemporaryPlayRail = ({
  controller,
  revealGrade = true,
}: {
  controller: TemporaryPlayController | null;
  revealGrade?: boolean;
}) => {
  if (!controller) return null;
  const { continuationSan, grade, moves, origin, replySource, status } = controller.state;
  if (moves.length === 0 && status !== "evaluating") return null;
  const verdict = grade?.verdict;
  const showGrade = revealGrade && grade?.status === "ready" && verdict != null;
  const verified = status === "ready" && (showGrade || replySource != null);
  const positive = showGrade && (grade.verdict === "holds" || grade.verdict === "forces_mate");
  const color = showGrade ? (positive ? "success.main" : "warning.main") : "text.secondary";
  const heading =
    temporaryPlayProgressCopy(status) ??
    (status === "unavailable"
      ? "This line could not be verified."
      : showGrade
        ? grade.attemptedSan.endsWith("#")
          ? "That is checkmate."
          : verdictCopy[verdict]
        : revealGrade && grade && grade.status !== "pending"
          ? "Your move could not be evaluated."
          : "Explore your idea.");
  return (
    <Box
      component="section"
      role="status"
      aria-live="polite"
      data-testid="temporary-play-rail"
      data-idea-receipt="true"
      sx={{
        borderBottom: layoutTokens.dividerBorder,
        borderColor: "divider",
        py: 2,
        px: { xs: 1, sm: 1.5 },
        mb: 2,
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          color: "text.secondary",
          fontSize: "caption.fontSize",
          letterSpacing: typographyTokens.tracking.label,
          mb: 2,
        }}
      >
        {verified ? "NIKOLA · VERIFIED" : "NIKOLA · IDEA CHECK"}
      </Typography>
      <Typography
        component="h2"
        sx={{
          fontFamily: "fontFamily",
          fontSize: { xs: "h5.fontSize", sm: "h4.fontSize" },
          fontWeight: "fontWeightBold",
          letterSpacing: typographyTokens.tracking.heading,
          lineHeight: typographyTokens.lineHeight.heading,
        }}
      >
        {heading}
      </Typography>
      {showGrade && (
        <Box data-testid="temporary-move-grade" sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, color }}>
            {positive ? <CheckCircleOutlineIcon fontSize="small" /> : <InfoOutlinedIcon fontSize="small" />}
            <Typography sx={{ fontSize: "caption.fontSize", fontWeight: "fontWeightBold" }}>
              {positive ? "IDEA HOLDS" : "WORTH REVISITING"}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontFamily: "fontFamily",
              fontSize: "h3.fontSize",
              fontWeight: "fontWeightBold",
              letterSpacing: typographyTokens.tracking.heading,
              my: 1,
            }}
          >
            {grade.attemptedSan}
          </Typography>
          <Typography
            sx={{ fontFamily: "fontFamily", fontSize: "body1.fontSize", lineHeight: typographyTokens.lineHeight.body }}
          >
            {grade.verdict === "holds"
              ? "Your move keeps the evaluation close to where it started."
              : grade.consequence}
            {grade.evaluation?.cp != null &&
              ` ${grade.evaluation.cp < 0 ? "The evaluation favors your opponent." : grade.evaluation.cp > 0 ? "The evaluation favors you." : "The evaluation is level."}`}
          </Typography>
          {grade.evaluation && (
            <Box
              component="details"
              sx={{
                mt: 1.5,
                fontSize: "caption.fontSize",
                color: "text.secondary",
                "& summary": {
                  cursor: "pointer",
                  minHeight: layoutTokens.controlHeight,
                  display: "flex",
                  alignItems: "center",
                },
              }}
            >
              <summary>Evaluation details · {formatEvaluation(grade.evaluation)}</summary>
              {grade.verdict === "holds" && (
                <Typography sx={{ fontSize: "caption.fontSize", mb: 1 }}>{grade.consequence}</Typography>
              )}
              <Typography sx={{ fontSize: "caption.fontSize", lineHeight: typographyTokens.lineHeight.body }}>
                After your move, from your perspective. Positive favors you; negative favors your opponent. Mate scores
                describe a forced finish.
              </Typography>
              {origin.recordedSan && (
                <Typography sx={{ mt: 1, fontSize: "caption.fontSize" }}>
                  Recorded move: {origin.recordedSan}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}
      {replySource && continuationSan.length > 0 && (
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: layoutTokens.dividerBorder, borderColor: "divider" }}>
          <Typography component="h3" sx={{ fontSize: "caption.fontSize", fontWeight: "fontWeightBold" }}>
            Verified line
          </Typography>
          <Typography sx={{ mt: 0.5, color: "text.secondary", fontSize: "caption.fontSize" }}>
            {replySource === "saved_continuation" ? "Saved continuation" : "Fresh analysis"} · reply and continuation
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
            {continuationSan.slice(0, 3).map((san, index) => (
              <Box
                key={`${index}-${san}`}
                sx={{ border: layoutTokens.dividerBorder, borderColor: "divider", px: 1.25, py: 1 }}
              >
                <Typography sx={{ color: "text.secondary", fontSize: "caption.fontSize" }}>PLY {index + 1}</Typography>
                <Typography sx={{ fontSize: "body1.fontSize", fontWeight: "fontWeightBold" }}>{san}</Typography>
              </Box>
            ))}
          </Box>
          {continuationSan.length > 3 && (
            <Box
              component="details"
              sx={{
                mt: 1,
                color: "text.secondary",
                fontSize: "caption.fontSize",
                "& summary": {
                  cursor: "pointer",
                  minHeight: layoutTokens.controlHeight,
                  display: "flex",
                  alignItems: "center",
                },
              }}
            >
              <summary>Full line · {continuationSan.length} plies</summary>
              <Typography sx={{ fontSize: "body2.fontSize", lineHeight: typographyTokens.lineHeight.body }}>
                {continuationSan.join(" · ")}
              </Typography>
            </Box>
          )}
        </Box>
      )}
      <Typography sx={{ mt: 1, color: "text.secondary", fontSize: "caption.fontSize", textAlign: "center" }}>
        Original game unchanged
      </Typography>
    </Box>
  );
};
